/* eslint-disable max-lines */
import { useForm, useFormState } from 'react-final-form'
import cx from 'classnames'
import { useFlags } from 'launchdarkly-react-client-sdk'

import Button from 'common/components/button'
import DynamicFields from 'common/components/dynamicFields'
import { FieldsConfiguration } from 'common/components/dynamicFields/interfaces'
import Textarea from 'common/components/textarea/Textarea'
import { useAppSelector } from 'common/hooks/redux'
import { ArrowRight as ArrowRightIcon, Spinner, Triangle } from 'common/icons'
import { useContent } from 'features/unifiedFlow/contentPage/hooks'

import { BRIEF_ELEMENT_ID } from '../../components/universalBriefElement/constants'
import { getStepStatus } from '../../helpers'
import ApplyBriefToAllTemplatesCheckbox from '../components/ApplyBriefToAllTemplatesCheckbox'

import { BriefAnalysisResult } from './hooks/useDebouncedBriefAnalysis'
import BrandVoiceSettings from './brandVoiceSettings'
import { PageState } from './BriefPage'

type Props = {
  isDisabled: boolean
  isLoading: boolean
  fieldsConfiguration: FieldsConfiguration
  pageState: PageState
  brandVoiceId?: string
  isOptimized?: boolean
  briefField?: any
  briefText?: string
  setBriefText?: (text: string) => void
  activeTab?: 'describe' | 'paste'
  setActiveTab?: (tab: 'describe' | 'paste') => void
  selectedCustomerJourney?: any
  setShowCustomerJourneyModal?: (show: boolean) => void
  isBriefSectionCollapsed?: boolean
  setIsBriefSectionCollapsed?: (collapsed: boolean) => void
  briefQuality?: {
    criteria: Array<{ met: boolean; score: number; label: string }>
    percentage: number
    quality: string
  }
  isAnalyzingBrief?: boolean
  briefAnalysisError?: Error | null
  analysisResult?: BriefAnalysisResult | null
  hasSavedBrief?: boolean
  hasBriefChanged?: boolean
}

const DynamicForm = ({
  isDisabled,
  fieldsConfiguration,
  isLoading,
  pageState,
  brandVoiceId,
  isOptimized,
  briefField,
  briefText,
  setBriefText,
  activeTab,
  setActiveTab,
  selectedCustomerJourney,
  setShowCustomerJourneyModal,
  isBriefSectionCollapsed,
  setIsBriefSectionCollapsed,
  briefQuality,
  isAnalyzingBrief,
  briefAnalysisError,
  analysisResult,
  hasSavedBrief,
  hasBriefChanged,
}: Props) => {
  const form = useForm()
  const formState = useFormState()
  const { content } = useContent()
  const flags = useFlags()

  const selectedElementId = useAppSelector(
    (state) => state.unifiedFlow.selectedElementId
  )
  const isUniversalBriefSelected = selectedElementId === BRIEF_ELEMENT_ID

  const briefStepsStatus = getStepStatus(content?.steps_status, 'brief')

  const nonBriefFields = briefField
    ? fieldsConfiguration?.filter((field) => field.name !== briefField.name)
    : fieldsConfiguration

  const isNewFlow = !!briefField

  return (
    <>
      <div className="w-full">
        {!isNewFlow ? (
          <div className="w-150">
            <DynamicFields
              fieldsConfiguration={fieldsConfiguration}
              isDisabled={isDisabled}
            />
          </div>
        ) : (
          nonBriefFields &&
          nonBriefFields.length > 0 && (
            <div className="w-150 mb-6">
              <DynamicFields
                fieldsConfiguration={nonBriefFields}
                isDisabled={isDisabled}
              />
            </div>
          )
        )}

        {isNewFlow && briefField && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-medium text-base-700">BRIEF</h2>
              </div>
              <div className="flex items-center gap-4">
                {!isNewFlow && (
                  <button
                    type="button"
                    className="text-sm text-base-700 hover:underline"
                  >
                    ADVANCED SETTINGS
                  </button>
                )}
                <button
                  type="button"
                  onClick={() =>
                    setIsBriefSectionCollapsed?.(!isBriefSectionCollapsed)
                  }
                  className="text-sm text-base-700 hover:underline flex items-center gap-1"
                >
                  {isBriefSectionCollapsed ? 'SHOW' : 'HIDE'}
                  <span>{isBriefSectionCollapsed ? '↓' : '↑'}</span>
                </button>
              </div>
            </div>

            {!isBriefSectionCollapsed && (
              <>
                <div className="mb-6">
                  <div className="text-base-700 font-medium mb-2">
                    Customer journey
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCustomerJourneyModal?.(true)}
                    className="w-full p-3 border border-gold-200 rounded text-left text-base-700 bg-gold-40 hover:bg-gold-100 transition-colors flex items-center justify-between"
                  >
                    <span>
                      {selectedCustomerJourney?.customerJourneyTitle || ''}
                    </span>
                    <svg
                      className="w-4 h-4 text-base-700"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  <div className="text-gold-700 text-xs font-normal leading-normal mt-2">
                    What stage of the customer journey is this for?
                  </div>
                </div>

                <div className="mb-2 ">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-base-700 font-medium">
                      Describe your brief
                    </div>
                    {briefText !== undefined && (
                      <div className="text-gold-500 text-xs font-normal">
                        {briefText?.length || 0} / 500
                      </div>
                    )}
                  </div>
                  <Textarea
                    value={briefText || ''}
                    onChange={(e) => setBriefText?.(e.target.value)}
                    placeholder="Write this in natural language and only include the details you want included in your content. We'll use your input to generate a brief."
                    maxLength={500}
                    showCharacterCount={false}
                    style={{ minHeight: '70px' }}
                    customClassName={
                      briefText && briefText.length > 0 && briefText.length < 20
                        ? 'bg-gold-40 border-1 text-base-700 font-normal text-sm rounded-sm leading-4 px-4 py-3 placeholder-gold-700 outline-none w-full border-orange-500 hover:border-orange-500 focus:border-orange-500 active:border-orange-500'
                        : 'bg-gold-40 border-1 text-base-700 font-normal text-sm rounded-sm leading-4 px-4 py-3 placeholder-gold-700 outline-none w-full border-gold-200 hover:border-gold-200 focus:border-gold-200 active:border-gold-200'
                    }
                  />
                </div>
                {briefText && briefText.length > 0 && briefText.length < 20 && (
                  <div className="flex items-center gap-2 mb-4">
                    <Triangle
                      className="w-4 h-4 text-orange-500 flex-shrink-0"
                      isDefaultColor={false}
                    />
                    <span className="text-sm text-base-700">
                      Short input. Try to provide more details for better copy
                      results.
                    </span>
                  </div>
                )}
                {isNewFlow && (
                  <p className="text-gold-700 text-xs font-normal leading-normal mt-2 mb-8">
                    Write this in natural language and only include the details
                    you want included in your content. We'll use your input to
                    generate a brief.
                  </p>
                )}

                {selectedCustomerJourney?.labels &&
                  selectedCustomerJourney.labels.length > 0 &&
                  (isAnalyzingBrief ||
                    hasBriefChanged ||
                    !hasSavedBrief ||
                    !!analysisResult) && (
                    <div className="mb-6 bg-gold-100 border border-gold-200 rounded-sm p-4">
                      <div className="mb-4">
                        <span className="text-base-700 font-medium">
                          Brief quality
                          {briefQuality?.quality &&
                            (briefQuality.criteria.length > 0 ||
                              analysisResult) && (
                              <span>: {briefQuality.quality}</span>
                            )}
                        </span>
                        {isAnalyzingBrief && (
                          <Spinner
                            className="w-4 h-4 text-base-700 animate-spin ml-2"
                            isDefaultColor={false}
                          />
                        )}
                      </div>
                      {briefAnalysisError && (
                        <div className="text-sm text-red-600 mb-4">
                          Error analyzing brief: {briefAnalysisError.message}
                        </div>
                      )}
                      <div className="space-y-2">
                        {selectedCustomerJourney.labels.map((label, index) => {
                          const criterion = briefQuality?.criteria.find(
                            (c) => c.label === label
                          )

                          const hasAnalysis = !!criterion || !!analysisResult
                          const score = criterion?.score ?? 0
                          const isClearlyMet = score === 1
                          const isPartiallyMet = score === 0.5
                          const isAbsent = hasAnalysis && score === 0

                          return (
                            <div
                              key={index}
                              className="flex items-center gap-2"
                            >
                              <div
                                className={cx(
                                  'w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center border-2',
                                  {
                                    'bg-gold-100 border-green-500':
                                      isClearlyMet,
                                    'bg-orange-200 border-orange-400':
                                      isPartiallyMet,
                                    'border-red-500': isAbsent,
                                    'border-gold-300': !hasAnalysis,
                                  }
                                )}
                              >
                                {isClearlyMet && (
                                  <svg
                                    className="w-2.5 h-2.5 text-green-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={3}
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                )}
                              </div>
                              <span className="text-sm text-gold-700">
                                {label}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
              </>
            )}
          </>
        )}
        {briefStepsStatus === 'active' &&
          flags.showBriefingV2 &&
          pageState === 'generateBrief' &&
          (isNewFlow ? true : isUniversalBriefSelected) && (
            <ApplyBriefToAllTemplatesCheckbox />
          )}
        {brandVoiceId && !isOptimized && (
          <hr className="w-full my-6 border-gold-400" />
        )}
        {brandVoiceId && !isOptimized && (
          <BrandVoiceSettings
            brandVoiceId={brandVoiceId}
            isDisabled={isDisabled}
          />
        )}
      </div>
      {!isNewFlow && pageState === 'generateBrief' && (
        <div className="flex pt-6 justify-end items-center">
          <Button
            type="submit"
            data-cy="content-generate-brief-button"
            data-testid="content-generate-brief-button"
            variant="primary"
            disabled={formState.invalid || isLoading}
            suffixIcon={<ArrowRightIcon isDefaultColor={false} size={4} />}
            loading={isLoading}
            onClick={() => {
              form.change('submitAction', 'generateBrief')
            }}
          >
            Generate brief
          </Button>
        </div>
      )}
      {!isNewFlow &&
        (pageState === 'formChangedWithBrief' ||
          pageState === 'formChangedWithTopics') && (
          <div className="flex pt-6 justify-end items-center">
            <Button
              variant="secondary"
              className="mr-4"
              disabled={isLoading}
              onClick={() => form?.reset()}
            >
              Revert
            </Button>
            <Button
              type="submit"
              data-cy="content-refresh-brief-button"
              data-testid="content-refresh-brief-button"
              variant="primary"
              suffixIcon={<ArrowRightIcon isDefaultColor={false} size={4} />}
              loading={isLoading}
              disabled={formState.invalid || isLoading}
              onClick={() => {
                form.change('submitAction', 'refreshBrief')
              }}
            >
              Refresh brief
            </Button>
          </div>
        )}
      {pageState === 'generateWithoutTopics' && (
        <div className="flex pt-6 justify-end items-center">
          <Button
            type="submit"
            data-cy="content-generate-brief-button"
            data-testid="content-generate-brief-button"
            variant="primary"
            disabled={formState.invalid || isLoading}
            suffixIcon={<ArrowRightIcon isDefaultColor={false} size={4} />}
            loading={isLoading}
            onClick={() => {
              form.change('submitAction', 'generateWithoutTopics')
            }}
          >
            Generate
          </Button>
        </div>
      )}
      {pageState === 'formChangedWithoutTopics' && (
        <div className="flex pt-6 justify-end items-center">
          <Button
            variant="secondary"
            className="mr-4"
            disabled={isLoading}
            onClick={() => form?.reset()}
          >
            Revert
          </Button>
          <Button
            type="submit"
            data-cy="content-regenerate-variants-button"
            data-testid="content-regenerate-variants-button"
            variant="primary"
            loading={isLoading}
            disabled={formState.invalid || isLoading}
            suffixIcon={<ArrowRightIcon isDefaultColor={false} size={4} />}
            onClick={() => {
              form.change('submitAction', 'generateWithoutTopics')
            }}
          >
            Regenerate
          </Button>
        </div>
      )}
      {pageState === 'brandVoiceChanged' && (
        <div className="flex pt-6 justify-end items-center">
          <Button
            variant="secondary"
            className="mr-4"
            disabled={isLoading}
            onClick={() => form?.reset()}
          >
            Revert
          </Button>
          <Button
            type="submit"
            data-cy="content-regenerate-button"
            data-testid="content-regenerate-button"
            variant="primary"
            loading={isLoading}
            disabled={formState.invalid || isLoading}
            onClick={() => {
              form.change('submitAction', 'brandVoiceChanged')
            }}
          >
            Regenerate
          </Button>
        </div>
      )}
    </>
  )
}

export default DynamicForm
